import { z } from 'zod';
import { dec } from '../lib/decimal';
import { numericString, zEmail, zIsoDateTime, zMoney, zQty, zSha256Hex, zUuid } from './common';
/**
 * Change orders (WU-14) — first-class signed change orders against a
 * sent/viewed/accepted estimate (optionally linked to its project).
 *
 * Flow: draft → priced lines (price to client; credits negative) → send
 * (freezes ONE immutable hash-bound version document + tokenized link
 * /p/c/<token>) → client consent → accept (typed signature) or decline.
 * Accepted change orders roll up into a revised contract total ON READ.
 *
 * BOUNDARY: amounts come only from the contractor's own lines; no
 * compliant / verdict / confidence field exists anywhere here. Math lives in
 * lib/change-order-math.ts (decimal.js). Schema authority:
 * api/db/migrations/0023_change_orders.sql.
 */
// ---------------------------------------------------------------- primitives
/** Positive quantity string, numeric(14,4), > 0. */
const zQtyPositive = numericString(14, 4, { nonNegative: true }).refine((v) => /[1-9]/.test(v), {
    message: 'quantity must be greater than 0',
});
/** Tax rate percentage string, numeric(7,4), 0..100 (e.g. "8.25"). */
export const zChangeOrderTaxRatePct = numericString(7, 4, { nonNegative: true }).refine((v) => dec(v).lte(100), { message: 'tax rate must be between 0 and 100' });
// -------------------------------------------------------------------- status
export const CHANGE_ORDER_STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'declined', 'void'];
export const ChangeOrderStatusSchema = z.enum(CHANGE_ORDER_STATUSES);
/** Legal transitions (DB-enforced; mirrored here as data for API/UI gating). */
export const CHANGE_ORDER_TRANSITIONS = {
    draft: ['sent', 'void'],
    sent: ['viewed', 'accepted', 'declined', 'void'],
    viewed: ['accepted', 'declined', 'void'],
    accepted: [],
    declined: [],
    void: [],
};
export const CHANGE_ORDER_EVENT_KINDS = ['viewed', 'consent_given', 'signed_copy_viewed'];
export const ChangeOrderEventKindSchema = z.enum(CHANGE_ORDER_EVENT_KINDS);
export const ChangeOrderResponseKindSchema = z.enum(['accepted', 'declined']);
// --------------------------------------------------------------------- lines
export const ChangeOrderLineSchema = z.object({
    id: zUuid,
    position: z.number().int().min(0),
    description: z.string(),
    qty: zQty,
    unit: z.string().nullable(),
    /** Price to the client; negative = credit. */
    unitPrice: zMoney,
    taxable: z.boolean(),
    /** roundMoney(qty × unitPrice) — derived, never stored. */
    extended: zMoney,
});
/** One line in PUT /change-orders/:id/lines. Position = array index. */
export const ChangeOrderLineWriteSchema = z
    .object({
    id: zUuid.optional(),
    description: z.string().trim().min(1).max(300),
    qty: zQtyPositive,
    unit: z.string().trim().max(32).nullable().optional(),
    /** May be negative (credit). */
    unitPrice: zMoney,
    taxable: z.boolean(),
})
    .strict();
// --------------------------------------------------------------- change order
export const ChangeOrderTotalsSchema = z.object({
    subtotal: zMoney,
    tax: zMoney,
    total: zMoney,
});
/** Revised contract total, derived on read from ACCEPTED change orders only. */
export const ContractTotalsSchema = z.object({
    originalTotal: zMoney,
    approvedChanges: zMoney,
    revisedTotal: zMoney,
});
export const ChangeOrderClientLinkSchema = z.object({
    /** Raw client URL — only returned right after send (raw token never stored). */
    url: z.string().nullable(),
    expiresAt: zIsoDateTime.nullable(),
});
export const ChangeOrderResponseSummarySchema = z.object({
    kind: ChangeOrderResponseKindSchema,
    signerName: z.string().nullable(),
    signedAt: zIsoDateTime.nullable(),
    declineReason: z.string().nullable(),
});
export const ChangeOrderSchema = z.object({
    id: zUuid,
    estimateId: zUuid,
    estimateNumber: z.string().nullable(),
    projectId: zUuid.nullable(),
    number: z.string(),
    title: z.string(),
    description: z.string(),
    reason: z.string(),
    scheduleImpactDays: z.number().int().nullable(),
    taxRatePct: z.string(),
    status: ChangeOrderStatusSchema,
    lines: z.array(ChangeOrderLineSchema),
    /** Drafts: derived from lines. Sent+: the frozen version document totals. */
    totals: ChangeOrderTotalsSchema,
    sentAt: zIsoDateTime.nullable(),
    viewedAt: zIsoDateTime.nullable(),
    decidedAt: zIsoDateTime.nullable(),
    voidedAt: zIsoDateTime.nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
    clientLink: ChangeOrderClientLinkSchema.nullable(),
    response: ChangeOrderResponseSummarySchema.nullable(),
});
/** List row — a change order without its lines. */
export const ChangeOrderSummarySchema = ChangeOrderSchema.omit({ lines: true });
/** GET /estimates/:id/change-orders · GET /projects/:id/change-orders. */
export const ChangeOrdersListResponseSchema = z.object({
    items: z.array(ChangeOrderSummarySchema),
    contract: ContractTotalsSchema,
});
// ------------------------------------------------------------------- requests
export const CreateChangeOrderRequestSchema = z
    .object({
    estimateId: zUuid,
    projectId: zUuid.optional(),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(20000).optional(),
    reason: z.string().max(2000).optional(),
    scheduleImpactDays: z.number().int().min(-3650).max(3650).optional(),
    taxRatePct: zChangeOrderTaxRatePct.optional(),
})
    .strict();
/**
 * PATCH /change-orders/:id. Content fields are draft-only (DB-locked once
 * sent); `projectId` may change in any status.
 */
export const UpdateChangeOrderRequestSchema = z
    .object({
    projectId: zUuid.nullable(),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(20000),
    reason: z.string().max(2000),
    scheduleImpactDays: z.number().int().min(-3650).max(3650).nullable(),
    taxRatePct: zChangeOrderTaxRatePct,
})
    .partial()
    .strict();
/** PUT /change-orders/:id/lines — full replacement, draft only. */
export const ReplaceChangeOrderLinesRequestSchema = z
    .object({
    lines: z.array(ChangeOrderLineWriteSchema).max(200),
})
    .strict();
/** POST /change-orders/:id/send — freezes the version and mints the client link. */
export const SendChangeOrderRequestSchema = z
    .object({
    recipientName: z.string().trim().min(1).max(200),
    recipientEmail: zEmail,
    expiresInDays: z.number().int().min(1).max(90).default(30),
    message: z.string().max(2000).optional(),
})
    .strict();
export const SendChangeOrderResponseSchema = z.object({
    changeOrder: ChangeOrderSchema,
    clientUrl: z.string(),
});
// ----------------------------------------------------------- frozen document
/**
 * The FROZEN change-order version document (change_order_versions.document).
 * content_hash = canonicalJsonHash(document) (see changeOrderDocumentHash).
 *
 * Every object is .strict() and carries NO transforms: parsing must never
 * strip or rewrite a key, or a re-hash would no longer match the stored hash.
 */
export const ChangeOrderDocumentLineSchema = z
    .object({
    description: z.string(),
    qty: zQty,
    unit: z.string().nullable(),
    unitPrice: zMoney,
    taxable: z.boolean(),
    extended: zMoney,
})
    .strict();
export const ChangeOrderDocumentSchema = z
    .object({
    kind: z.literal('change_order'),
    version: z.literal(1),
    company: z
        .object({
        name: z.string(),
        logoFileId: zUuid.nullable(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        licenseLine: z.string().nullable(),
    })
        .strict(),
    client: z
        .object({
        name: z.string(),
        contactName: z.string().nullable(),
    })
        .strict(),
    estimate: z
        .object({
        number: z.string(),
        title: z.string(),
        originalTotal: zMoney,
    })
        .strict(),
    changeOrder: z
        .object({
        number: z.string(),
        title: z.string(),
        description: z.string(),
        reason: z.string(),
        scheduleImpactDays: z.number().int().nullable(),
        lines: z.array(ChangeOrderDocumentLineSchema),
        subtotal: zMoney,
        tax: zMoney,
        taxRatePct: z.string(),
        total: zMoney,
    })
        .strict(),
    contract: z
        .object({
        originalTotal: zMoney,
        /** Accepted change orders on this estimate at the moment of issue. */
        previouslyApprovedChanges: zMoney,
        revisedTotalIfAccepted: zMoney,
    })
        .strict(),
    issuedAt: zIsoDateTime,
    /** Plain-language tooling-not-advice note; never a compliance assertion. */
    boundaryNote: z.string(),
})
    .strict();
// -------------------------------------------------------------------- public
/** GET /public/change-orders/:token (records a `viewed` event). */
export const PublicChangeOrderViewSchema = z.object({
    document: ChangeOrderDocumentSchema,
    contentHash: zSha256Hex,
    status: ChangeOrderStatusSchema,
    /** ESIGN consent text shown before the sign step (snapshotted on accept). */
    consentText: z.string().min(1),
    /** Consent already recorded for this version+token (unlocks the sign step). */
    consentGivenAt: zIsoDateTime.nullable(),
    /** False once responded, voided, or the link expired. */
    canRespond: z.boolean(),
    response: ChangeOrderResponseSummarySchema.nullable(),
    expiresAt: zIsoDateTime,
});
/** POST /public/change-orders/:token/consent — explicit ESIGN consent. */
export const PublicChangeOrderConsentRequestSchema = z
    .object({
    agree: z.literal(true),
})
    .strict();
export const PublicChangeOrderConsentResponseSchema = z.object({
    ok: z.literal(true),
    consentGivenAt: zIsoDateTime,
});
/**
 * POST /public/change-orders/:token/accept — ESIGN intent: explicit checkbox +
 * typed signature, hash-bound to the exact document the signer saw.
 * Attribution (ip/ua/time) is captured server-side.
 */
export const PublicChangeOrderAcceptRequestSchema = z
    .object({
    signerName: z.string().trim().min(1).max(200),
    signerEmail: zEmail.optional(),
    typedSignature: z.string().trim().min(1).max(200),
    intentConfirmed: z.literal(true),
    /** Must equal the version content_hash the client was shown. */
    documentHash: zSha256Hex,
})
    .strict();
export const PublicChangeOrderAcceptResponseSchema = z.object({
    ok: z.literal(true),
    responseId: zUuid,
    signedAt: zIsoDateTime,
});
/** POST /public/change-orders/:token/decline. signerName falls back to the token recipient. */
export const PublicChangeOrderDeclineRequestSchema = z
    .object({
    reason: z.string().trim().max(2000).optional(),
    signerName: z.string().trim().min(1).max(200).optional(),
})
    .strict();
export const PublicChangeOrderDeclineResponseSchema = z.object({
    ok: z.literal(true),
    respondedAt: zIsoDateTime,
});
//# sourceMappingURL=change-orders.js.map